import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "./Header";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

const isGuestUser = () => {
  return !localStorage.getItem("token") || !sessionStorage.getItem("userid");
};

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCheckoutOptions, setShowCheckoutOptions] = useState(false);
  const navigate = useNavigate();

  const fetchCartItems = async () => {
    try {
      if (isGuestUser()) {
        const guestCart = JSON.parse(localStorage.getItem("guest_cart")) || [];
        setCartItems(guestCart);
      } else {
        const token = localStorage.getItem("token");
        const userId = sessionStorage.getItem("userid");

        const response = await axios.get(`${API_URL}/api/cart/getusercart/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const products = response.data.cart?.products || [];
        setCartItems(products);
      }
    } catch (err) {
      console.error("Error fetching cart:", err);
      setError("Failed to load cart. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCartItems();
  }, []);

  const updateCartState = (response) => {
    const updatedProducts = response.data?.cart?.products || [];
    setCartItems(updatedProducts);
  };

  const getProductId = (item) => {
    if (item.productId && typeof item.productId === "object") {
      return item.productId._id || item.productId.id;
    }
    return item.productId;
  };

  const saveGuestCart = (items) => {
    localStorage.setItem("guest_cart", JSON.stringify(items));
    setCartItems(items);
  };

  const handleGuestIncrease = (index) => {
    const updated = [...cartItems];
    updated[index].quantity += 1;
    saveGuestCart(updated);
  };

  const handleGuestDecrease = (index) => {
    const updated = [...cartItems];
    if (updated[index].quantity > 1) {
      updated[index].quantity -= 1;
      saveGuestCart(updated);
    } else {
      handleGuestRemove(index);
    }
  };

  const handleGuestRemove = (index) => {
    const updated = [...cartItems];
    updated.splice(index, 1);
    saveGuestCart(updated);
  };

  const handleIncreaseQuantity = async (item) => {
    try {
      const token = localStorage.getItem("token");
      const userId = sessionStorage.getItem("userid");
      const productId = getProductId(item);

      const response = await axios.post(
        `${API_URL}/api/cart/increase`,
        { productId, userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      updateCartState(response);
    } catch (error) {
      console.error("Error increasing quantity:", error);
      setError("Failed to increase quantity.");
    }
  };

  const handleDecreaseQuantity = async (item) => {
    try {
      const token = localStorage.getItem("token");
      const userId = sessionStorage.getItem("userid");
      const productId = getProductId(item);

      const response = await axios.post(
        `${API_URL}/api/cart/decrease`,
        { productId, userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      updateCartState(response);
    } catch (error) {
      console.error("Error decreasing quantity:", error);
      setError("Failed to decrease quantity.");
    }
  };

  const handleRemoveFromCart = async (item) => {
    try {
      const token = localStorage.getItem("token");
      const userId = sessionStorage.getItem("userid");
      const productId = getProductId(item);

      const response = await axios.delete(
        `${API_URL}/api/cart/remove/${productId}?userId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      updateCartState(response);
    } catch (error) {
      console.error("Error removing item:", error);
      setError("Failed to remove product.");
    }
  };

  const totalPrice = cartItems.reduce(
    (total, item) =>
      total + (isGuestUser() ? item.price : item.productId?.price || 0) * (item.quantity || 1),
    0
  );

  return (
    <div className="max-w-4xl mx-auto p-6 pt-20 relative">
      <Header />
      <h2 className="text-2xl font-bold mb-6 text-center">Shopping Cart</h2>

      {loading ? (
        <p className="text-gray-600 text-center">Loading...</p>
      ) : error ? (
        <p className="text-red-500 text-center">{error}</p>
      ) : cartItems.length === 0 ? (
        <p className="text-gray-600 text-center">Your cart is empty.</p>
      ) : (
        <div className="flex flex-col">
          {cartItems.map((item, index) => {
            const image = isGuestUser()
              ? item.image
              : item.productId?.image || "https://via.placeholder.com/80";

            const title = isGuestUser()
              ? item.title
              : item.productId?.title || "Unknown Product";

            const price = isGuestUser() ? item.price : item.productId?.price || 0;

            return (
              <div
                key={isGuestUser() ? index : getProductId(item)}
                className="flex flex-row items-center bg-white shadow-md p-4 mb-4 rounded-lg w-full"
              >
                <img
                  src={image}
                  alt={title}
                  className="w-20 h-20 object-cover rounded"
                />

                <div className="flex-1 ml-4">
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-gray-500">₹{price}</p>
                </div>

                <div className="flex flex-row items-center space-x-3">
                  <span className="text-gray-600">Qty: {item.quantity || 1}</span>

                  {isGuestUser() ? (
                    <>
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg"
                        onClick={() => handleGuestIncrease(index)}
                      >
                        +
                      </button>
                      <button
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-lg"
                        onClick={() => handleGuestDecrease(index)}
                      >
                        -
                      </button>
                      <button
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg"
                        onClick={() => handleGuestRemove(index)}
                      >
                        🗑
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg"
                        onClick={() => handleIncreaseQuantity(item)}
                      >
                        +
                      </button>
                      <button
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-lg"
                        onClick={() => handleDecreaseQuantity(item)}
                      >
                        -
                      </button>
                      <button
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg"
                        onClick={() => handleRemoveFromCart(item)}
                      >
                        🗑
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          <div className="text-center sm:text-right mt-6">
            <h3 className="text-lg font-semibold">Total: ₹{totalPrice.toFixed(2)}</h3>
            <button
              className="bg-green-500 text-white px-5 py-2 rounded-lg mt-4 w-full sm:w-auto"
              onClick={() => {
                if (isGuestUser()) {
                  setShowCheckoutOptions(true);
                } else {
                  navigate("/checkout");
                }
              }}
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      )}

      {showCheckoutOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4">Proceed to Checkout</h3>
            <p className="mb-6">Choose how you'd like to continue:</p>
            <div className="flex flex-col space-y-3">
              <button
                className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                onClick={() => navigate("/login")}
              >
                Login to Proceed
              </button>
              <button
                className="bg-green-600 text-white py-2 rounded hover:bg-green-700"
                onClick={() =>
                  navigate("/guest-checkout", {
                    state: { cartItems, totalPrice },
                  })
                }
              >
                Continue as Guest
              </button>
              <button
                className="mt-2 text-sm text-gray-500 hover:underline"
                onClick={() => setShowCheckoutOptions(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
